import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';

/**
 * InlineNotice — aviso dentro de modal/sheet/form aberto (REDLINE-feedback §5).
 *
 * <p>Generalização do antigo `InlineError` (que era danger-only) pras 4
 * variantes: danger (erro do form), success (resultado revisável — ex.: "97
 * importados · 3 falhas"), warn (parcial/atenção), info (aviso neutro).
 *
 * <p>É o alvo pra RESULTADO REVISÁVEL — dado que o user precisa ler, conferir
 * e às vezes comparar com o arquivo. Não é evento (Toast) nem estado de tela
 * (PageBanner) — é resultado que vive com a ação, no lugar onde ela ocorreu.
 *
 * <p>Base Bootstrap: `<Alert>`, vestido com `--bs-alert-*` (redline §2, §5).
 * Mesma caixa em todas as variantes (flex + padding + radius) — muda só o
 * `bg` / `border` / cor do glifo, análogo às variantes do PageBanner.
 *
 * <p>Contrato de props textuais (title/cause/children): TEXTO já resolvido
 * (via `t()`) ou ReactNode (`<Trans i18nKey="..."/>`). NÃO envolvemos em
 * `<Trans>` aqui — antes gerava `missingKey` no i18next.
 *
 * @param variant     'danger' | 'success' | 'warn' | 'info'. Default: 'danger'.
 * @param title       string ou node — "o que aconteceu"
 * @param cause       string ou node opcional — "por quê" / detalhe complementar
 * @param dismissible bool — renderiza × no canto superior direito. Default false.
 *                    Use quando o user pode escolher fechar (caso persistente
 *                    dentro de modal que segue aberto).
 * @param onDismiss   () => void — chamado ao clicar no ×. Obrigatório se
 *                    `dismissible` está setado.
 * @param actionLabel string ou node opcional — rótulo do botão de ação (ex.:
 *                    "Tentar de novo"). Usado quando a falha aconteceu DEPOIS
 *                    do usuário investir algo na tela (form preenchido) — a
 *                    superfície não pode ser trocada (perderia trabalho), mas
 *                    a ação de recuperação é oferecida junto.
 * @param onAction    () => void — chamado ao clicar no botão. Obrigatório se
 *                    `actionLabel` está setado.
 * @param actionBusy  bool — desabilita o botão e mostra spinner enquanto a
 *                    ação assíncrona roda (mesmo pattern do LoadErrorState).
 */
export default function InlineNotice({
	variant = 'danger',
	title,
	cause,
	dismissible = false,
	onDismiss,
	actionLabel,
	onAction,
	actionBusy = false,
}) {
	if (!title && !cause) return null;
	// Bootstrap Alert só aceita 'danger'|'success'|'warning'|'info'|... — mapa 'warn' → 'warning'.
	const bsVariant = variant === 'warn' ? 'warning' : variant;
	const Glyph = GLYPHS[variant] || GLYPHS.danger;
	// role='alert' pra danger; 'status' pros demais (aria-live polite).
	const role = variant === 'danger' ? 'alert' : 'status';
	return (
		<Alert variant={bsVariant} className={`iln iln-${variant}`} role={role}>
			<span className="iln-ic" aria-hidden="true"><Glyph /></span>
			<div className="iln-txt">
				{title && <div className="iln-title">{title}</div>}
				{cause && <div className="iln-cause">{cause}</div>}
			</div>
			{actionLabel && onAction && (
				<Button
					className="iln-cta"
					size="sm"
					variant={variant === 'danger' ? 'outline-danger' : 'outline-secondary'}
					onClick={onAction}
					disabled={actionBusy}
				>
					{actionBusy ? <Spinner animation="border" size="sm" /> : actionLabel}
				</Button>
			)}
			{dismissible && (
				<button
					type="button"
					className="iln-x"
					onClick={onDismiss}
					aria-label="Fechar"
				>
					<GlyphX />
				</button>
			)}
		</Alert>
	);
}

/* ─────────── Glyphs Lucide inline (stroke-linecap round obrigatório) ───────────
 * `stroke-linecap="round"` também no dot do "!" (M12 17h.01) — sem isso o ponto
 * desaparece. */
const GlyphAlertCircle = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3}>
		<circle cx="12" cy="12" r="9" />
		<line x1="12" y1="8" x2="12" y2="12" />
		<line x1="12" y1="16" x2="12.01" y2="16" />
	</svg>
);
const GlyphCheckCircle = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3}>
		<circle cx="12" cy="12" r="9" />
		<polyline points="8 12 11 15 16 9" />
	</svg>
);
const GlyphAlertTri = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3}>
		<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
		<path d="M12 9v4" />
		<path d="M12 17h.01" />
	</svg>
);
const GlyphInfoCircle = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3}>
		<circle cx="12" cy="12" r="9" />
		<path d="M12 11v5" />
		<path d="M12 7h.01" />
	</svg>
);
const GlyphX = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4}>
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

const GLYPHS = {
	danger:  GlyphAlertCircle,
	success: GlyphCheckCircle,
	warn:    GlyphAlertTri,
	info:    GlyphInfoCircle,
};
